import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import MuiChip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// Icons
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

const items = [
  {
    icon: <AutoAwesomeRoundedIcon />,
    titleKey: "features.builder.title",
    descriptionKey: "features.builder.description",
    imageLight: `url("/images/Builder de layout.png")`,
    imageDark: `url(/images/Builder de layout.png)`, 
  },
  {
    icon: <CalendarTodayRoundedIcon />,
    titleKey: "features.scheduling.title",
    descriptionKey: "features.scheduling.description",
    imageLight: `url("/images/Gestion des prieres.png")`,
    imageDark: `url(/images/Gestion des prieres.png)`, 
  },
  {
    icon: <CampaignRoundedIcon />,
    titleKey: "features.communityAnnouncements.title",
    descriptionKey: "features.communityAnnouncements.description",
    imageLight: `url("/images/Gestion des evenements.png")`,
    imageDark: `url(/images/Gestion des evenements.png)`, 
  },
  {
    icon: <DevicesRoundedIcon />,
    titleKey: "features.mobileapp.title",
    descriptionKey: "features.mobileapp.description",
    imageLight: `url("/images/Mobile App.jpeg")`,
    imageDark: `url(/images/Mobile App.jpeg)`, 
  },
];

const Chip = styled(MuiChip)(({ theme }) => ({
  variants: [
    {
      props: ({ selected }) => selected,
      style: {
        background:
          "linear-gradient(to bottom right, hsl(210, 98%, 48%), hsl(210, 98%, 35%))",
        color: "hsl(0, 0%, 100%)",
        borderColor: (theme.vars || theme).palette.primary.light,
        "& .MuiChip-label": {
          color: "hsl(0, 0%, 100%)",
        },
        ...theme.applyStyles("dark", {
          borderColor: (theme.vars || theme).palette.primary.dark,
        }),
      },
    },
  ],
}));

export default function Features() {
  const { t } = useTranslation();
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);

  const handleItemClick = (index) => {
    setSelectedItemIndex(index);
  };

  return (
    <section id="features">
      <Container  id="features" sx={{ py: { xs: 4, sm: 4 } }}>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: "column", width: { sm: "100%", md: "60%" } }}>
          <Typography component="h2" variant="h4" gutterBottom sx={{ color: "text.primary" }}>
            {t("features.title")}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: { xs: 2, sm: 4 } }}>
            {t("features.subtitle")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row-reverse" }, gap: 2 }}>
          <div>
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                flexDirection: "column",
                gap: 2,
                height: "100%",
              }}
            >
              {items.map(({ icon, titleKey, descriptionKey }, index) => (
                <Box
                  key={index}
                  component={Button}
                  onClick={() => handleItemClick(index)}
                  sx={[
                    (theme) => ({
                      p: 2,
                      height: "100%",
                      width: "100%",
                      "&:hover": {
                        backgroundColor: (theme.vars || theme).palette.action.hover,
                      },
                    }),
                    selectedItemIndex === index && {
                      backgroundColor: "action.selected",
                    },
                  ]}
                >
                  <Box
                    sx={[
                      {
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "left",
                        gap: 1,
                        textAlign: "left",
                        textTransform: "none",
                        color: "text.secondary",
                      },
                      selectedItemIndex === index && {
                        color: "text.primary",
                      },
                    ]}
                  >
                    {icon}
                    <Typography variant="h6">{t(titleKey)}</Typography>
                    <Typography variant="body2">{t(descriptionKey)}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </div>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              width: { xs: '100%', md: '70%' },
              height: 'var(--items-image-height)',
            }}
          >
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                width: '100%',
                display: { xs: 'none', sm: 'flex' },
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={(theme) => ({
                  m: 'auto',
                  width: 520,
                  height: 600,
                  backgroundSize: 'contain',
                  backgroundImage: 'var(--items-imageLight)',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  ...theme.applyStyles('dark', {
                    backgroundImage: 'var(--items-imageDark)',
                  }),
                })}
                style={
                  items[selectedItemIndex]
                    ? {
                        '--items-imageLight': items[selectedItemIndex].imageLight,
                        '--items-imageDark': items[selectedItemIndex].imageDark,
                      }
                    : {}
                }
              />
            </Card>
          </Box>
        </Box>
      </Container>
    </section>
  );
}